import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

export const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email,
        role: 'client',
        createdAt: new Date().toISOString()
      });
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err?.code === 'auth/configuration-not-found') {
        setError('Firebase Authentication is not enabled. Please enable it in the Firebase Console.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-mono text-xs uppercase tracking-widest">Back to Home</span>
        </button>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-xl shadow-2xl">
          <div className="mb-8 text-center flex flex-col items-center">
            <div className="relative w-32 h-32 rounded-full p-0.5 bg-[#261006] border border-[#caa062]/80 shadow-[0_8px_24px_rgba(0,0,0,0.9),inset_0_1.5px_2px_rgba(255,255,255,0.25)] shadow-md mb-3">
              <img 
                src="/brand_logo.png?v=4" 
                alt="The Brown Pelican Man" 
                referrerPolicy="no-referrer"
                className="w-full h-full rounded-full object-contain" 
              />
            </div>
            <h2 className="text-2xl font-bold mb-1 tracking-tight text-white">Create Account</h2>
            <p className="text-gray-400 text-xs font-light">Join The Brown Pelican Man client portal.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[#d8aa68] mb-2">Email Address</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#d8aa68]/60 focus:bg-black/60 transition-all placeholder-white/20"
                placeholder="name@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-[#d8aa68] mb-2">Password</label>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#d8aa68]/60 focus:bg-black/60 transition-all placeholder-white/20"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={loading}
              className="w-full text-[#ffe0a3] border border-[#d8aa68]/50 font-bold py-4 rounded-lg uppercase tracking-widest text-xs hover:border-[#ffe0a3] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2 shadow-lg"
              style={{
                background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)'
              }}
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : 'Create Account'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
